import { z } from 'zod';
/** Shared by every schema module here: a parse never throws, it returns a typed result. */
export function toSchemaResult(parsed) {
    if (parsed.success)
        return { ok: true, value: parsed.data };
    const issues = parsed.error.issues.map((issue) => ({
        path: issue.path.map((segment) => String(segment)).join('.') || '(root)',
        message: issue.message,
    }));
    const first = issues[0];
    return {
        ok: false,
        issues,
        message: first ? `${first.path}: ${first.message}` : 'payload did not match schema',
    };
}
/** Timestamps arrive as ISO strings from GitHub and as `Date` from the driver. Accept both. */
const TimestampLike = z.union([z.string(), z.date()]);
/** `numeric` columns come back from `pg` as strings. Accept both rather than coercing. */
const NumericLike = z.union([z.string(), z.number()]);
export const GitHubUserSchema = z.looseObject({
    login: z.string().optional(),
    id: z.union([z.number(), z.string()]).optional(),
    type: z.string().optional(),
});
export const GitHubLabelSchema = z.looseObject({
    id: z.union([z.number(), z.string()]).optional(),
    name: z.string().optional(),
    color: z.string().optional(),
});
export const GitHubMilestoneSchema = z.looseObject({
    number: z.number().optional(),
    title: z.string().optional(),
    state: z.string().optional(),
});
export const GitHubRepositorySchema = z.looseObject({
    id: z.union([z.number(), z.string()]).optional(),
    name: z.string().optional(),
    full_name: z.string().optional(),
    owner: GitHubUserSchema.nullish(),
    private: z.boolean().optional(),
});
export const GitHubInstallationSchema = z.looseObject({
    id: z.union([z.number(), z.string()]).optional(),
    node_id: z.string().optional(),
});
/**
 * `state` stays a bare string. Modelling it as an enum would reject the payload outright the
 * day GitHub adds a third value; `normalizeIssueState` narrows it at the point of use.
 */
export const GitHubIssueSchema = z.looseObject({
    id: z.union([z.number(), z.string()]).optional(),
    node_id: z.string().optional(),
    number: z.number(),
    title: z.string(),
    body: z.string().nullish(),
    state: z.string().optional(),
    state_reason: z.string().nullish(),
    user: GitHubUserSchema.nullish(),
    assignee: GitHubUserSchema.nullish(),
    assignees: z.array(GitHubUserSchema).nullish(),
    labels: z.array(z.union([z.string(), GitHubLabelSchema])).nullish(),
    milestone: GitHubMilestoneSchema.nullish(),
    html_url: z.string().optional(),
    comments: z.number().optional(),
    created_at: TimestampLike.nullish(),
    updated_at: TimestampLike.nullish(),
    closed_at: TimestampLike.nullish(),
    /** Present only on pull requests. Its presence is the ONLY way to tell a PR from an issue. */
    pull_request: z.looseObject({ url: z.string().optional() }).nullish(),
});
export const GitHubIssueCommentSchema = z.looseObject({
    id: z.union([z.number(), z.string()]).optional(),
    body: z.string().nullish(),
    user: GitHubUserSchema.nullish(),
    html_url: z.string().optional(),
    /**
     * The API URL of the issue this comment belongs to, ending in the issue NUMBER:
     * `https://api.github.com/repos/{owner}/{repo}/issues/{number}`.
     *
     * The webhook path never needs it — an `issue_comment` delivery carries the whole issue
     * alongside the comment. The repository-wide sweep in `apps/cron/src/comments.ts` has
     * nothing else: `GET /repos/{owner}/{repo}/issues/comments` returns comments with no
     * embedded issue and no issue id, so this string is the only link back to the anchor row.
     */
    issue_url: z.string().optional(),
    created_at: TimestampLike.nullish(),
    updated_at: TimestampLike.nullish(),
});
/** Every webhook body carries this much. Used to route before the event-specific parse. */
export const WebhookEnvelopeSchema = z.looseObject({
    action: z.string().optional(),
    repository: GitHubRepositorySchema.nullish(),
    installation: GitHubInstallationSchema.nullish(),
    sender: GitHubUserSchema.nullish(),
});
/**
 * Actions we act on. Others parse fine and are ignored by the router.
 *
 * The assignment and milestone actions are here because they change columns the board reads
 * — `assigned` with no mirror leaves an issue showing the previous assignee indefinitely.
 * Each of them carries the full issue object, so the handler needs no special case for them.
 */
export const ISSUE_ACTIONS = [
    'opened',
    'edited',
    'closed',
    'reopened',
    'labeled',
    'assigned',
    'unassigned',
    'milestoned',
    'demilestoned',
];
export const ISSUE_COMMENT_ACTIONS = ['created', 'edited', 'deleted'];
export const IssuesEventSchema = z.looseObject({
    action: z.string(),
    issue: GitHubIssueSchema,
    label: GitHubLabelSchema.nullish(),
    changes: z.looseObject({}).nullish(),
    repository: GitHubRepositorySchema.nullish(),
    installation: GitHubInstallationSchema.nullish(),
    sender: GitHubUserSchema.nullish(),
});
export const IssueCommentEventSchema = z.looseObject({
    action: z.string(),
    issue: GitHubIssueSchema,
    comment: GitHubIssueCommentSchema,
    changes: z.looseObject({}).nullish(),
    repository: GitHubRepositorySchema.nullish(),
    installation: GitHubInstallationSchema.nullish(),
    sender: GitHubUserSchema.nullish(),
});
/**
 * The stored issue row. Every persisted column is declared — an undeclared column would be
 * erased on the first load-modify-write — and the enum columns use `.catch()` so a value
 * written by a future version degrades to a safe default instead of failing the whole row.
 */
export const StoredIssueSchema = z.looseObject({
    id: z.union([z.string(), z.number()]).optional(),
    organization_id: z.string(),
    project_id: z.string(),
    github_issue_id: z.union([z.string(), z.number()]).optional(),
    github_issue_number: z.number(),
    title: z.string(),
    body: z.string().nullish(),
    state: z.enum(['open', 'closed']).catch('open'),
    author_login: z.string().nullish(),
    assignee_login: z.string().nullish(),
    milestone_title: z.string().nullish(),
    labels: z.array(z.string()).nullish(),
    html_url: z.string().optional(),
    /** Fails closed: an unreadable kind means a human classifies it, never a billable guess. */
    kind: z
        .enum(['maintenance', 'feature', 'needs_triage'])
        .catch('needs_triage'),
    /** Fails closed: an unreadable approval state is undecided, never approved. */
    approval_status: z
        .enum(['approved', 'not_approved', 'pending'])
        .catch('pending'),
    imported: z.boolean().catch(false),
    size: z
        .enum(['XS', 'S', 'M', 'L', 'XL'])
        .nullish()
        .catch(null),
    estimated_hours: NumericLike.nullish(),
    actual_hours: NumericLike.nullish(),
    no_charge: z.boolean().catch(false),
    /*
     * Added by migrations 0007 through 0010. The schema went eight columns stale before anyone
     * noticed, because nothing in the application reads through it — and a declaration that
     * claims to cover every persisted column while missing a quarter of them is worse than none,
     * since the next person to reach for it inherits the gap. `stored-issue-columns.test.ts`
     * now compares this against the migrations, so it cannot drift again in silence.
     */
    suggested_guidance: z.string().nullish(),
    suggestion_accepted_by: z.string().nullish(),
    submitted_by_user_id: z.string().nullish(),
    submitted_by_name: z.string().nullish(),
    attention_requested_at: TimestampLike.nullish(),
    attention_answered_at: TimestampLike.nullish(),
    attention_requested_by: z.string().nullish(),
    attention_requested_by_name: z.string().nullish(),
    attention_note: z.string().nullish(),
    /** Migration 0020. Nullish like every other optional timestamp: absent means never started. */
    started_at: TimestampLike.nullish(),
    github_created_at: TimestampLike.nullish(),
    github_updated_at: TimestampLike.nullish(),
    closed_at: TimestampLike.nullish(),
    created_at: TimestampLike.nullish(),
    updated_at: TimestampLike.nullish(),
});
export function parseWebhookEnvelope(input) {
    return toSchemaResult(WebhookEnvelopeSchema.safeParse(input));
}
export function parseIssuesEvent(input) {
    return toSchemaResult(IssuesEventSchema.safeParse(input));
}
export function parseIssueCommentEvent(input) {
    return toSchemaResult(IssueCommentEventSchema.safeParse(input));
}
export function parseStoredIssue(input) {
    return toSchemaResult(StoredIssueSchema.safeParse(input));
}
/**
 * GitHub's issues endpoints return pull requests as issues; a `pull_request` key is the only
 * difference. Not filtering fills a client's board with our own PRs.
 */
export function isPullRequestPayload(issue) {
    if (typeof issue !== 'object' || issue === null)
        return false;
    if (!('pull_request' in issue))
        return false;
    return issue.pull_request !== undefined && issue.pull_request !== null;
}
/** Narrow GitHub's open-ended `state` string. Unknown means open: never mark work delivered. */
export function normalizeIssueState(state) {
    return state === 'closed' ? 'closed' : 'open';
}
//# sourceMappingURL=github.js.map